import { type ByteSource, fourcc, i64, u64, viewOf } from './bytes';

const MAC_EPOCH_MS = Date.UTC(1904, 0, 1);
const HEADER = 8;
const LARGE_HEADER = 16;
const VISUAL_ENTRY_FIELDS = 78;

export type Mp4ErrorKind = 'no-moov' | 'truncated' | 'unsupported';

export class Mp4Error extends Error {
  constructor(readonly kind: Mp4ErrorKind, message: string) {
    super(message);
    this.name = 'Mp4Error';
  }
}

export interface SampleTable {
  readonly count: number;
  readonly offsets: Float64Array;
  readonly sizes: Uint32Array;
  readonly times: Float64Array;
  readonly keyframes: Uint8Array;
}

export interface AvcEntry {
  readonly kind: 'avc';
  readonly codec: string;
  readonly width: number;
  readonly height: number;
  readonly avcC: Uint8Array;
}

export type SampleEntry = AvcEntry | { readonly kind: 'other'; readonly codec: string };

export interface Track {
  readonly id: number;
  readonly enabled: boolean;
  readonly handler: string;
  readonly timescale: number;
  readonly duration: number;
  readonly entry: SampleEntry;
  readonly chapterTrackIds: readonly number[];
  readonly samples: SampleTable;
}

export interface Movie {
  readonly createdAt: number | undefined;
  readonly duration: number;
  readonly tracks: readonly Track[];
  readonly chpl: Uint8Array | undefined;
}

interface Box {
  readonly type: string;
  readonly start: number;
  readonly body: number;
  readonly end: number;
}

interface Edit {
  readonly mediaTime: number;
  readonly delay: number;
}

const fail = (kind: Mp4ErrorKind, message: string): never => {
  throw new Mp4Error(kind, message);
};

function readBox(view: DataView, offset: number, limit: number): Box {
  if (offset + HEADER > limit) fail('truncated', 'box header runs past its parent');
  const size32 = view.getUint32(offset);
  const type = fourcc(view, offset + 4);
  const large = size32 === 1;
  if (large && offset + LARGE_HEADER > limit) fail('truncated', `${type} header runs past its parent`);
  const size = large ? u64(view, offset + 8) : size32 === 0 ? limit - offset : size32;
  const body = offset + (large ? LARGE_HEADER : HEADER);
  if (offset + size < body || offset + size > limit) fail('truncated', `${type} box runs past its parent`);
  return { type, start: offset, body, end: offset + size };
}

function* children(view: DataView, parent: Box, skip = 0): Generator<Box> {
  for (let offset = parent.body + skip; offset + HEADER <= parent.end; ) {
    const box = readBox(view, offset, parent.end);
    yield box;
    offset = box.end;
  }
}

function find(view: DataView, parent: Box, type: string, skip = 0): Box | undefined {
  for (const box of children(view, parent, skip)) if (box.type === type) return box;
  return undefined;
}

const need = (view: DataView, parent: Box, type: string): Box =>
  find(view, parent, type) ?? fail('unsupported', `${parent.type} has no ${type}`);

const findPath = (view: DataView, root: Box, path: readonly string[]): Box | undefined =>
  path.reduce<Box | undefined>((box, type) => box && find(view, box, type), root);

const isVersion1 = (view: DataView, box: Box): boolean => view.getUint8(box.body) === 1;

const copyBody = (view: DataView, box: Box, skip = 0): Uint8Array =>
  new Uint8Array(view.buffer, view.byteOffset + box.body + skip, box.end - box.body - skip).slice();

function ensureEntries(box: Box, first: number, count: number, width: number): void {
  if (first + count * width > box.end) fail('truncated', `${box.type} lists more entries than it holds`);
}

async function locateMoov(source: ByteSource): Promise<{ readonly start: number; readonly size: number }> {
  for (let offset = 0; offset + HEADER <= source.size; ) {
    const head = await source.read(offset, Math.min(LARGE_HEADER, source.size - offset));
    const box = readBox(viewOf(head), 0, source.size - offset);
    if (box.type === 'moov') return { start: offset, size: box.end };
    if (box.type === 'moof') fail('unsupported', 'fragmented MP4');
    offset += box.end;
  }
  return fail('no-moov', 'no moov box, the recording was probably cut off');
}

export async function readMovie(source: ByteSource): Promise<Movie> {
  const { start, size } = await locateMoov(source);
  const view = viewOf(await source.read(start, size));
  return parseMoov(view, readBox(view, 0, size));
}

function parseMoov(view: DataView, moov: Box): Movie {
  if (find(view, moov, 'mvex')) fail('unsupported', 'fragmented MP4');
  const mvhd = need(view, moov, 'mvhd');
  const v1 = isVersion1(view, mvhd);
  const created = v1 ? u64(view, mvhd.body + 4) : view.getUint32(mvhd.body + 4);
  const timescale = view.getUint32(mvhd.body + (v1 ? 20 : 12));
  const duration = v1 ? u64(view, mvhd.body + 24) : view.getUint32(mvhd.body + 16);
  if (timescale === 0) fail('unsupported', 'movie timescale is zero');
  const tracks = [...children(view, moov)].filter((box) => box.type === 'trak').map((trak) => parseTrack(view, trak, timescale));
  const chpl = findPath(view, moov, ['udta', 'chpl']);
  return {
    createdAt: created === 0 ? undefined : MAC_EPOCH_MS + created * 1000,
    duration: duration / timescale,
    tracks,
    chpl: chpl && copyBody(view, chpl),
  };
}

function parseTrack(view: DataView, trak: Box, movieTimescale: number): Track {
  const tkhd = need(view, trak, 'tkhd');
  const mdia = need(view, trak, 'mdia');
  const mdhd = need(view, mdia, 'mdhd');
  const stbl = findPath(view, mdia, ['minf', 'stbl']) ?? fail('unsupported', 'track has no sample table');
  const v1 = isVersion1(view, mdhd);
  const timescale = view.getUint32(mdhd.body + (v1 ? 20 : 12));
  if (timescale === 0) fail('unsupported', 'track timescale is zero');
  const duration = v1 ? u64(view, mdhd.body + 24) : view.getUint32(mdhd.body + 16);
  const edit = parseEdit(view, findPath(view, trak, ['edts', 'elst']), movieTimescale);
  return {
    id: view.getUint32(tkhd.body + (isVersion1(view, tkhd) ? 20 : 12)),
    enabled: (view.getUint32(tkhd.body) & 1) === 1,
    handler: fourcc(view, need(view, mdia, 'hdlr').body + 8),
    timescale,
    duration: duration / timescale,
    entry: parseSampleEntry(view, need(view, stbl, 'stsd')),
    chapterTrackIds: parseChapterReference(view, findPath(view, trak, ['tref', 'chap'])),
    samples: parseSampleTable(view, stbl, timescale, edit),
  };
}

function parseChapterReference(view: DataView, chap: Box | undefined): readonly number[] {
  if (!chap) return [];
  return Array.from({ length: (chap.end - chap.body) >> 2 }, (_, i) => view.getUint32(chap.body + i * 4));
}

function parseEdit(view: DataView, elst: Box | undefined, movieTimescale: number): Edit {
  if (!elst) return { mediaTime: 0, delay: 0 };
  const v1 = isVersion1(view, elst);
  const width = v1 ? 20 : 12;
  const count = view.getUint32(elst.body + 4);
  ensureEntries(elst, elst.body + 8, count, width);
  let delay = 0;
  for (let i = 0; i < count; i++) {
    const at = elst.body + 8 + i * width;
    const segment = v1 ? u64(view, at) : view.getUint32(at);
    const mediaTime = v1 ? i64(view, at + 8) : view.getInt32(at + 4);
    if (mediaTime !== -1) return { mediaTime, delay };
    delay += segment / movieTimescale;
  }
  return { mediaTime: 0, delay };
}

function parseSampleEntry(view: DataView, stsd: Box): SampleEntry {
  const entry = readBox(view, stsd.body + 8, stsd.end);
  if (entry.type !== 'avc1' && entry.type !== 'avc3') return { kind: 'other', codec: entry.type };
  const avcC = find(view, entry, 'avcC', VISUAL_ENTRY_FIELDS) ?? fail('unsupported', `${entry.type} without avcC`);
  return {
    kind: 'avc',
    codec: entry.type,
    width: view.getUint16(entry.body + 24),
    height: view.getUint16(entry.body + 26),
    avcC: copyBody(view, avcC),
  };
}

function parseSampleTable(view: DataView, stbl: Box, timescale: number, edit: Edit): SampleTable {
  if (find(view, stbl, 'stz2')) fail('unsupported', 'compact sample sizes (stz2)');
  const sizes = parseSizes(view, need(view, stbl, 'stsz'));
  const count = sizes.length;
  const decodeTimes = parseDecodeTimes(view, need(view, stbl, 'stts'), count);
  const offsets = parseCompositionOffsets(view, find(view, stbl, 'ctts'), count);
  const times = new Float64Array(count);
  for (let i = 0; i < count; i++) {
    times[i] = (decodeTimes[i]! + offsets[i]! - edit.mediaTime) / timescale + edit.delay;
  }
  return {
    count,
    offsets: parseSampleOffsets(view, stbl, sizes),
    sizes,
    times,
    keyframes: parseKeyframes(view, find(view, stbl, 'stss'), count),
  };
}

function parseSizes(view: DataView, stsz: Box): Uint32Array {
  const fixed = view.getUint32(stsz.body + 4);
  const count = view.getUint32(stsz.body + 8);
  if (fixed !== 0) return new Uint32Array(count).fill(fixed);
  ensureEntries(stsz, stsz.body + 12, count, 4);
  return Uint32Array.from({ length: count }, (_, i) => view.getUint32(stsz.body + 12 + i * 4));
}

function parseDecodeTimes(view: DataView, stts: Box, count: number): Float64Array {
  const entries = view.getUint32(stts.body + 4);
  ensureEntries(stts, stts.body + 8, entries, 8);
  const times = new Float64Array(count);
  let sample = 0;
  let time = 0;
  for (let e = 0; e < entries && sample < count; e++) {
    const run = view.getUint32(stts.body + 8 + e * 8);
    const delta = view.getUint32(stts.body + 12 + e * 8);
    for (let k = 0; k < run && sample < count; k++, time += delta) times[sample++] = time;
  }
  if (sample < count) fail('truncated', 'stts covers fewer samples than stsz');
  return times;
}

function parseCompositionOffsets(view: DataView, ctts: Box | undefined, count: number): Float64Array {
  const offsets = new Float64Array(count);
  if (!ctts) return offsets;
  const signed = isVersion1(view, ctts);
  const entries = view.getUint32(ctts.body + 4);
  ensureEntries(ctts, ctts.body + 8, entries, 8);
  let sample = 0;
  for (let e = 0; e < entries && sample < count; e++) {
    const at = ctts.body + 8 + e * 8;
    const run = view.getUint32(at);
    offsets.fill(signed ? view.getInt32(at + 4) : view.getUint32(at + 4), sample, Math.min(count, sample + run));
    sample += run;
  }
  return offsets;
}

function parseKeyframes(view: DataView, stss: Box | undefined, count: number): Uint8Array {
  if (!stss) return new Uint8Array(count).fill(1);
  const entries = view.getUint32(stss.body + 4);
  ensureEntries(stss, stss.body + 8, entries, 4);
  const keyframes = new Uint8Array(count);
  for (let i = 0; i < entries; i++) {
    const sample = view.getUint32(stss.body + 8 + i * 4) - 1;
    if (sample >= 0 && sample < count) keyframes[sample] = 1;
  }
  return keyframes;
}

function parseChunkOffsets(view: DataView, stbl: Box): Float64Array {
  const co64 = find(view, stbl, 'co64');
  const box = co64 ?? need(view, stbl, 'stco');
  const width = co64 ? 8 : 4;
  const count = view.getUint32(box.body + 4);
  ensureEntries(box, box.body + 8, count, width);
  return Float64Array.from({ length: count }, (_, i) =>
    co64 ? u64(view, box.body + 8 + i * 8) : view.getUint32(box.body + 8 + i * 4),
  );
}

function parseSampleOffsets(view: DataView, stbl: Box, sizes: Uint32Array): Float64Array {
  const chunks = parseChunkOffsets(view, stbl);
  const stsc = need(view, stbl, 'stsc');
  const runs = view.getUint32(stsc.body + 4);
  ensureEntries(stsc, stsc.body + 8, runs, 12);
  const offsets = new Float64Array(sizes.length);
  let sample = 0;
  for (let r = 0; r < runs; r++) {
    const at = stsc.body + 8 + r * 12;
    const firstChunk = view.getUint32(at) - 1;
    const lastChunk = r + 1 < runs ? view.getUint32(at + 12) - 1 : chunks.length;
    const perChunk = view.getUint32(at + 4);
    for (let chunk = firstChunk; chunk < lastChunk && sample < sizes.length; chunk++) {
      sample = placeChunk(offsets, sizes, sample, perChunk, chunks[chunk]!);
    }
  }
  if (sample < sizes.length) fail('truncated', 'chunks hold fewer samples than stsz lists');
  return offsets;
}

function placeChunk(offsets: Float64Array, sizes: Uint32Array, first: number, perChunk: number, chunkOffset: number): number {
  const end = Math.min(sizes.length, first + perChunk);
  let offset = chunkOffset;
  for (let sample = first; sample < end; sample++) {
    offsets[sample] = offset;
    offset += sizes[sample]!;
  }
  return end;
}

export type AvcTrack = Track & { readonly entry: AvcEntry };

const isAvcVideo = (track: Track): track is AvcTrack => track.handler === 'vide' && track.entry.kind === 'avc';

export const videoTrack = (movie: Movie): AvcTrack | undefined => movie.tracks.find(isAvcVideo);
